import { Box } from "@mui/material";

export const formatTime = (timeInSeconds) => {
    if (typeof timeInSeconds !== 'number' || timeInSeconds < 0) return '00:00:00'; // Manejo de errores

    const hours = Math.floor(timeInSeconds / 3600).toString().padStart(2, '0');
    const minutes = Math.floor((timeInSeconds % 3600) / 60).toString().padStart(2, '0');
    const seconds = (timeInSeconds % 60).toString().padStart(2, '0');

    return `${hours}:${minutes}:${seconds}`;
};

export const timeToMinutes = (time) => {
    if (!time) return null;
    const [h, m] = time.split(":").map(Number);
    return h * 60 + m;
};

export const timeToSeconds = (timeString) => {
    const [hours, minutes, seconds] = timeString.split(':').map(Number);
    return hours * 3600 + minutes * 60 + (seconds || 0);
};

export const calculateTimeDifference = (eventTime, turnTime, toleranceTime) => {
    const eventSeconds = timeToSeconds(eventTime);
    const differenceInSeconds = eventSeconds - turnTime;

    const differenceMinutes = Math.floor(Math.abs(differenceInSeconds) / 60);
    const differenceSeconds = Math.abs(differenceInSeconds) % 60;
    const sign = differenceInSeconds > 0 ? '+' : '-';

    return {
        status: differenceInSeconds > toleranceTime ? 'Atrasado' : differenceInSeconds < - toleranceTime ? 'Adelantado' : 'En tiempo',
        difference: `${sign} ${differenceMinutes} min ${differenceSeconds.toFixed(0)} seg`,
    };
};

export const findClosestTurn = (eventTime, scheduleTurns, zoneId) => {
    const eventSeconds = timeToSeconds(eventTime);

    let closestTurn = null;
    let smallestDifference = 7200;

    scheduleTurns.forEach((turn, index) => {
        if (turn.id === zoneId && turn.time !== null) {
            const difference = Math.abs(eventSeconds - turn.time);
            // console.log(eventSeconds, ' - ', turn.time, ' - ', difference, ' - ', smallestDifference, ' - ' ,turn.label)
            if (difference < smallestDifference) {
                closestTurn = { ...turn, checkpointIndex: index };
                smallestDifference = difference;
            }
        }
    });

    return closestTurn;
};

export const getAngleDifference = (a, b) => {
    const diff = Math.abs(a - b) % 360;
    return diff > 180 ? 360 - diff : diff;
};

export const isDirectionalRoutePoint = (routePoint) => {
    const orientation = Number(routePoint?.orientation);

    return (
        routePoint?.label?.includes('Ida') ||
        routePoint?.label?.includes('Vuelta') ||
        (!Number.isNaN(orientation) && orientation > 0)
    );
};

export const findClosestCheckpoint = (
    eventTime,
    scheduleCheckpoints,
    zoneId,
    orientations = null,
    userRoute = [],
) => {

    const eventSeconds = timeToSeconds(eventTime);

    let closestCheckpoint = null;
    let smallestDifference = 3600;

    // Buscar heading más cercano al evento
    let closestOrientation = null;

    if (Array.isArray(orientations) && orientations.length > 0) {
        const eventDate = new Date(`1970-01-01T${eventTime}`);

        orientations.forEach(o => {
            const orientationDate = new Date(o.time.replace(' ', 'T'));

            const diff = Math.abs(eventDate.getTime() - orientationDate.getTime());

            if (!closestOrientation || diff < closestOrientation.diff) {
                closestOrientation = {
                    ...o,
                    diff
                };
            }
        });
    }

    // Obtener orientación esperada
    const routePoint = userRoute.find(r => r.id === zoneId);

    const expectedOrientation = routePoint?.orientation ?? null;

    // Validar checkpoints
    scheduleCheckpoints.forEach((cp, index) => {
        if (cp.id !== zoneId || cp.time === null) return;

        // Validar rumbo
        if (closestOrientation && expectedOrientation !== null) {

            const diffAngle = getAngleDifference(
                closestOrientation.heading,
                expectedOrientation
            );

            if (diffAngle > 40) return; // Si la diferencia de ángulo es mayor a 40°, no es un checkpoint válido
        }

        // Validar tiempo
        const diffTime = Math.abs(eventSeconds - cp.time);

        if (diffTime < smallestDifference) {
            closestCheckpoint = {
                ...cp,
                checkpointIndex: index,
                matchedHeading: closestOrientation?.heading ?? null,
                expectedOrientation,
                angleDiff: closestOrientation
                    ? getAngleDifference(
                        closestOrientation.heading,
                        expectedOrientation
                    )
                    : null
            };

            smallestDifference = diffTime;
        }
    });

    return closestCheckpoint;
};

export const findClosestCheckpointOptimized = (eventSeconds, scheduleCheckpoints, routePoint, orientations = null) => {
    let closestCheckpoint = null;
    let smallestDiff = Infinity;

    //    1. Buscar orientación cercana
    let closestOrientation = null;

    if (Array.isArray(orientations) && orientations.length) {
        let left = 0;
        let right = orientations.length - 1;

        // Búsqueda binaria
        while (left <= right) {
            const mid = (left + right) >> 1;

            if (orientations[mid].seconds < eventSeconds) {
                left = mid + 1;
            } else {
                right = mid - 1;
            }
        }

        // Candidatos más cercanos
        const prev = orientations[right];
        const next = orientations[left];

        if (prev && next) {
            closestOrientation =
                Math.abs(prev.seconds - eventSeconds) <
                    Math.abs(next.seconds - eventSeconds)
                    ? prev
                    : next;
        } else {
            closestOrientation = prev || next;
        }
    }

    // 2. Obtener orientación esperada
    const expectedOrientation = routePoint?.orientation ?? null;

    // 3. Buscar checkpoint válido
    for (let i = 0; i < scheduleCheckpoints.length; i++) {
        const cp = scheduleCheckpoints[i];

        const normalizeLabel = (label) =>
            label
                .replace(' Ida', '')
                .replace(' Vuelta', '')
                .trim();

        if (
            cp.id !== routePoint.id ||
            cp.time == null
        ) continue;

        const cpLabel = normalizeLabel(cp.label);
        const routeLabel = normalizeLabel(routePoint.label);

        // Siempre comparar por nombre limpio
        if (cpLabel !== routeLabel) {
            continue;
        }

        // Validar ángulo
        const isDirectional = isDirectionalRoutePoint(routePoint);

        if (isDirectional && closestOrientation && expectedOrientation !== null) {
            const diffAngle = getAngleDifference(closestOrientation.heading, expectedOrientation);

            if (diffAngle > 40) continue;
        }

        // Validar tiempo
        const diffTime = Math.abs(eventSeconds - cp.time);

        if (diffTime < smallestDiff) {
            smallestDiff = diffTime;

            closestCheckpoint = {
                ...cp,
                checkpointIndex: i,
                matchedHeading: closestOrientation?.heading ?? null,
                expectedOrientation,
                angleDiff: closestOrientation
                    ? getAngleDifference(
                        closestOrientation.heading,
                        expectedOrientation
                    )
                    : null
            };
        }
    }

    return closestCheckpoint;
};

export const findClosestCheckpointOptimized2 = (eventSeconds, scheduleCheckpoints, routePoint, orientations = null) => {
    let closestCheckpoint = null;
    let smallestDiff = Infinity;

    //    1. Buscar orientación cercana
    let closestOrientation = null;

    if (Array.isArray(orientations) && orientations.length) {
        let left = 0;
        let right = orientations.length - 1;

        // Búsqueda binaria
        while (left <= right) {
            const mid = (left + right) >> 1;

            if (orientations[mid].seconds < eventSeconds) {
                left = mid + 1;
            } else {
                right = mid - 1;
            }
        }

        // Candidatos más cercanos
        const prev = orientations[right];
        const next = orientations[left];

        if (prev && next) {
            closestOrientation =
                Math.abs(prev.seconds - eventSeconds) <
                    Math.abs(next.seconds - eventSeconds)
                    ? prev
                    : next;
        } else {
            closestOrientation = prev || next;
        }
    }

    // 2. Obtener orientación esperada
    const expectedOrientation = routePoint?.orientation ?? null;

    // 3. Buscar checkpoint válido
    for (let i = 0; i < scheduleCheckpoints.length; i++) {

        const cp = scheduleCheckpoints[i];

        const normalizeLabel = (label) =>
            label
                .replace(' Ida', '')
                .replace(' Vuelta', '')
                .trim();

        if (
            cp.id !== routePoint.id ||
            cp.time == null
        ) continue;

        // Validar ángulo
        const isDirectional = isDirectionalRoutePoint(routePoint);

        if (isDirectional) {
            // Para Ida/Vuelta:
            // comparar nombre completo
            if (cp.label !== routePoint.label) {
                continue;
            }

        } else {
            // Para normales:
            // comparar nombre limpio
            const cpLabel = normalizeLabel(cp.label);
            const routeLabel = normalizeLabel(routePoint.label);

            if (cpLabel !== routeLabel) {
                continue;
            }
        }

        if (isDirectional) {
            // Si es direccional y no hay orientación,
            // descartar completamente
            if (
                !closestOrientation ||
                expectedOrientation == null
            ) {
                continue;
            }

            const diffAngle = getAngleDifference(
                closestOrientation.heading,
                expectedOrientation
            );

            // console.log({
            //     routePoint: routePoint.label,
            //     heading: closestOrientation?.heading,
            //     expectedOrientation,
            //     diffAngle
            // });

            if (diffAngle > 40) continue;
        }

        // console.log({
        //     routePoint: routePoint.label,
        //     cp: cp.label,
        //     heading: closestOrientation?.heading,
        //     expectedOrientation,
        //     diffAngle: closestOrientation
        //         ? getAngleDifference(
        //             closestOrientation.heading,
        //             expectedOrientation
        //         )
        //         : null
        // });
        // Validar tiempo
        const diffTime = Math.abs(eventSeconds - cp.time);

        if (diffTime < smallestDiff) {
            smallestDiff = diffTime;

            closestCheckpoint = {
                ...cp,
                checkpointIndex: i,
                matchedHeading: closestOrientation?.heading ?? null,
                expectedOrientation,
                angleDiff: closestOrientation
                    ? getAngleDifference(
                        closestOrientation.heading,
                        expectedOrientation
                    )
                    : null
            };
        }
    }

    return closestCheckpoint;
};

export const parseOrientationCSV = (csvText) => {
    if (!csvText) return [];

    const lines = csvText.trim().split('\n');

    // Quitar encabezado
    lines.shift();

    return lines.map(line => {
        const [time, heading] = line
            .replace(/"/g, '') // quitar comillas
            .split(',');

        return {
            time,
            heading: Number(heading)
        };
    });
};


// Componente auxiliar para mostrar los paneles según la pestaña activa
export function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            style={{ flex: 1, display: value !== index ? 'none' : 'flex', overflowY: 'hidden' }}
            {...other}
        >
            {value === index && <Box sx={{ p: 0, flex: 1, display: 'block', overflow: 'hidden' }}>{children}</Box>}
        </div>
    );
};

export function naturalSort(a, b) {
    // Normaliza valores para evitar errores
    const nameA = a && a.name ? a.name : '';
    const nameB = b && b.name ? b.name : '';

    // Si ambos son solo números (con o sin ceros), comparar como número
    if (/^\d+$/.test(nameA) && /^\d+$/.test(nameB)) {
        return parseInt(nameA, 10) - parseInt(nameB, 10);
    }

    // Extraer primer número en el string
    const matchA = nameA.match(/\d+/);
    const matchB = nameB.match(/\d+/);

    // Ambos tienen números en el texto
    if (matchA && matchB) {
        const numA = parseInt(matchA[0], 10);
        const numB = parseInt(matchB[0], 10);
        if (numA !== numB) return numA - numB;
        // Si el número es igual, comparar por el texto completo
        return nameA.localeCompare(nameB);
    }
    // Solo uno tiene número: el que tiene número va primero
    if (matchA) return -1;
    if (matchB) return 1;

    // Ninguno tiene número: comparar como texto
    return nameA.localeCompare(nameB);
}


export function mixedNaturalSort(a, b) {
    const regex = /^\d+/;
    const numA = a.label.match(regex);
    const numB = b.label.match(regex);

    if (numA && numB) {
        // Ambos inician con número
        const diff = parseInt(numA[0], 10) - parseInt(numB[0], 10);
        if (diff !== 0) return diff;
        // Si son iguales, ordenar por string completo
        return a.label.localeCompare(b.label);
    }
    if (numA) return -1; // numérico va antes
    if (numB) return 1;
    // Ninguno inicia con número: orden alfabético
    return a.label.localeCompare(b.label);
}

export const calculateDifferences = (turn, allTurns, index) => {
    return turn.map((point, pointIndex) => {
        // Obtener tiempos actuales y siguientes
        const currentTime = point.time ?? allTurns[index + 1]?.[pointIndex]?.time;
        const nextPoint = turn[(pointIndex + 1) % turn.length]; // Circular, último con el primero
        const nextTime =
            nextPoint?.time ?? allTurns[index + 1]?.[(pointIndex + 1) % turn.length]?.time;

        // Validar tiempos para evitar negativos
        if (currentTime !== null && nextTime !== null) {
            let diff = 0;
            if (nextTime > currentTime) {
                diff = nextTime - currentTime;
            } else {
                const newNextTime = allTurns[index + 1]?.[(pointIndex + 1) % turn.length]?.time;
                if (newNextTime) {
                    diff = newNextTime - currentTime;
                }
            }
            if (diff > 0) {
                const minutes = Math.floor(diff / 60);
                const seconds = diff % 60;
                return { minutes, seconds };
            }
        }

        return { minutes: 0, seconds: 0 }; // O puedes retornar null o "" si prefieres
    });
};
